import React, { useState } from 'react';
import { 
  TextField, 
  Button, 
  FormControl, 
  FormControlLabel, 
  RadioGroup, 
  Radio, 
  FormLabel, 
  Box, 
  Paper,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  IconButton,
  ImageList,
  ImageListItem,
  Stack,
  Fab,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress
} from '@mui/material';
import { collection, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, uploadBytesResumable } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { translations } from '../translations';
import ConfirmationPage from './ConfirmationPage';
import logo from './equitas-oht-logo.png';

const theme = {
  primary: '#EF582E', // Orange Equitas
  secondary: '#F17F5E', // Version plus claire de l'orange Equitas
  background: '#FFF9F5', // Fond légèrement orangé
  text: '#2D3748', // Gris foncé pour le texte
  fontFamily: '"Plus Jakarta Sans", sans-serif',
};

const languages = [
  { code: 'fr', name: 'Français' },
  { code: 'en', name: 'English' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'de', name: 'Deutsch' },
  { code: 'it', name: 'Italiano' },
  { code: 'es', name: 'Español' },
  { code: 'da', name: 'Dansk' },
  { code: 'tr', name: 'Türkçe' },
  { code: 'ar', name: 'العربية' }
];

const commonTextFieldStyle = {
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': {
      borderColor: theme.primary
    },
    '& textarea, & input': {
      fontFamily: theme.fontFamily,
      letterSpacing: '-0.3px'
    }
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: theme.primary
  },
  '& input::placeholder': {
    fontFamily: theme.fontFamily
  },
  '& textarea::placeholder': {
    fontFamily: theme.fontFamily
  }
};

const ReportForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    date: '',
    isPublic: 'public',
    platform: '',
    otherPlatform: '',
    link: '',
    comments: '',
    attachments: []
  });

  const [previewUrls, setPreviewUrls] = useState([]);
  const [language, setLanguage] = useState('fr');
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);

  const t = translations[language];

  const getTranslatedPlatforms = () => [
    { value: 'web', label: 'Web' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'twitter', label: 'X (Twitter)' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'tiktok', label: 'TikTok' },
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'youtube', label: 'YouTube' },
    { value: 'media', label: language === 'fr' ? 'Médias' :
                            language === 'en' ? 'Media' :
                            language === 'nl' ? 'Media' :
                            language === 'de' ? 'Medien' :
                            language === 'it' ? 'Media' :
                            language === 'es' ? 'Medios' :
                            language === 'da' ? 'Medier' :
                            language === 'tr' ? 'Medya' :
                            language === 'ar' ? 'وسائل الإعلام' : 'Media' },
    { value: 'other', label: t.other }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    const newPreviewUrls = files.map(file => URL.createObjectURL(file));
    
    const newProgress = {};
    files.forEach((file, index) => {
      newProgress[previewUrls.length + index] = 0;
    });
    
    setUploadProgress(prev => ({ ...prev, ...newProgress }));
    setPreviewUrls(prev => [...prev, ...newPreviewUrls]);
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));
  };

  const handleRemoveFile = (index) => {
    URL.revokeObjectURL(previewUrls[index]);
    
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.link && formData.attachments.length === 0) {
      alert(t.validationMessage);
      return;
    }

    try {
      setIsUploading(true);
      const attachmentUrls = [];
      
      for (const [index, file] of formData.attachments.entries()) {
        const storageRef = ref(storage, `attachments/${Date.now()}_${file.name}`);
        
        const uploadTask = uploadBytesResumable(storageRef, file);
        
        await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(prev => ({
                ...prev,
                [index]: progress
              }));
            },
            (error) => {
              reject(error);
            },
            async () => {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              attachmentUrls.push(url);
              resolve();
            }
          );
        });
      }

      const submissionData = {
        name: formData.name,
        email: formData.email,
        date: formData.date,
        isPublic: formData.isPublic,
        platform: formData.platform,
        otherPlatform: formData.platform === 'other' ? formData.otherPlatform : '',
        link: formData.link,
        comments: formData.comments,
        attachmentUrls,
        timestamp: new Date()
      };

      await addDoc(collection(db, 'reports'), submissionData);

      previewUrls.forEach(url => URL.revokeObjectURL(url));
      setUploadProgress({});
      setIsUploading(false);
      setSubmittedData(submissionData);
      setIsSubmitted(true);
      
    } catch (error) {
      setIsUploading(false);
      console.error("Erreur lors de l'envoi du signalement:", error);
      alert(t.errorMessage);
    }
  };

  const handleReturn = () => {
    setIsSubmitted(false);
    setSubmittedData(null);
    setFormData({
      name: '',
      email: '',
      date: '',
      isPublic: 'public',
      platform: '',
      otherPlatform: '',
      link: '',
      comments: '',
      attachments: []
    });
    setPreviewUrls([]);
  };

  if (isSubmitted && submittedData) {
    return (
      <ConfirmationPage
        submittedData={submittedData}
        onReturn={handleReturn}
        theme={theme}
        t={t}
      />
    );
  }

  return (
    <Box sx={{ 
      maxWidth: 800, 
      mx: 'auto', 
      p: 3, 
      mt: 4,
      fontFamily: theme.fontFamily,
      letterSpacing: '-0.3px',
      '& .MuiTypography-root': {
        fontFamily: theme.fontFamily,
        letterSpacing: '-0.3px',
      },
      '& .MuiButton-root': {
        fontFamily: theme.fontFamily,
        letterSpacing: '-0.3px',
      },
      '& .MuiInputLabel-root': {
        fontFamily: theme.fontFamily,
        letterSpacing: '-0.3px',
      },
      '& .MuiInput-root': {
        fontFamily: theme.fontFamily,
        letterSpacing: '-0.3px',
      },
      '& .MuiFormLabel-root': {
        fontFamily: theme.fontFamily,
        letterSpacing: '-0.3px',
      },
      '& .MuiMenuItem-root': {
        fontFamily: theme.fontFamily,
        letterSpacing: '-0.3px',
      }
    }}>
      <Box sx={{ 
        display: 'flex',
        justifyContent: 'center',
        mb: 4
      }}>
        <img 
          src={logo} 
          alt="Equitas Logo" 
          style={{ 
            height: '40px',
            width: 'auto'
          }} 
        />
      </Box>
      <Box sx={{ 
        mb: 4, 
        display: 'flex', 
        justifyContent: 'center',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            justifyContent: 'center',
            maxWidth: '100%',
          }}
        >
          {languages.map((lang) => (
            <Button
              key={lang.code}
              onClick={() => setLanguage(lang.code)}
              variant="text"
              size="small"
              sx={{
                minWidth: 'auto',
                fontSize: '0.75rem',
                bgcolor: language === lang.code ? theme.primary : 'transparent',
                border: `1px solid ${theme.primary}`,
                color: language === lang.code ? 'white' : theme.primary,
                '&:hover': {
                  bgcolor: language === lang.code ? theme.secondary : `${theme.primary}10`,
                  border: `1px solid ${theme.primary}`
                },
                boxShadow: 'none',
                fontWeight: language === lang.code ? 'bold' : 'normal',
                textTransform: 'none',
                px: 1,
                py: 0.5,
                ...(lang.code === 'ar' && {
                  fontFamily: 'Arial, sans-serif'
                })
              }}
            >
              {lang.name}
            </Button>
          ))}
        </Box>
      </Box>

      <Paper 
        elevation={0}
        sx={{ 
          p: 4,
          borderRadius: 2,
          backgroundColor: theme.background,
          border: `1px solid ${theme.primary}40`
        }}
      >
        <form onSubmit={handleSubmit}>
          <Box sx={{ 
            display: 'grid', 
            gap: 3,
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            '& .full-width': {
              gridColumn: { md: '1 / -1' }
            }
          }}>
            <Box className="full-width">
              <Typography 
                variant="h4" 
                component="h1" 
                gutterBottom
                sx={{ 
                  mb: 1,
                  color: theme.primary,
                  fontWeight: 'bold',
                  textAlign: 'center'
                }}
              >
                {t.title}
              </Typography>
              
              <Typography 
                variant="caption" 
                component="h2" 
                gutterBottom
                sx={{ 
                  mb: 4,
                  color: theme.text,
                  textAlign: 'center',
                  opacity: 0.7,
                  letterSpacing: '1px'
                }}
              >
                EQUITAS ONLINE HATE TRACKER
              </Typography>
            </Box>

            <TextField
              label={t.name}
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t.optional}
              variant="outlined"
              fullWidth
              sx={commonTextFieldStyle}
            />

            <TextField
              label={t.email}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              variant="outlined"
              fullWidth
              sx={commonTextFieldStyle}
            />

            <TextField
              label={t.date}
              name="date"
              type="date"
              value={formData.date}
              onChange={handleChange}
              required
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={commonTextFieldStyle}
            />

            <FormControl component="fieldset">
              <FormLabel sx={{ mb: 1, color: theme.text }}>{t.type}</FormLabel>
              <RadioGroup
                name="isPublic"
                value={formData.isPublic}
                onChange={handleChange}
                row
                sx={{
                  '& .MuiRadio-root.Mui-checked': {
                    color: theme.primary
                  }
                }}
              >
                <FormControlLabel 
                  value="public" 
                  control={<Radio />} 
                  label={t.public} 
                  sx={{ mr: 4 }}
                />
                <FormControlLabel 
                  value="private" 
                  control={<Radio />} 
                  label={t.private} 
                />
              </RadioGroup>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel sx={{ '&.Mui-focused': { color: theme.primary } }}>
                {t.platform}
              </InputLabel>
              <Select
                name="platform"
                value={formData.platform}
                onChange={handleChange}
                required
                label={t.platform}
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.primary
                  },
                  '& .MuiSelect-select': {
                    fontFamily: theme.fontFamily
                  },
                  '& .MuiMenuItem-root': {
                    fontFamily: theme.fontFamily
                  }
                }}
              >
                {getTranslatedPlatforms().map((platform) => (
                  <MenuItem 
                    key={platform.value} 
                    value={platform.value}
                    sx={{
                      fontFamily: theme.fontFamily
                    }}
                  >
                    {platform.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={t.link}
              name="link"
              value={formData.link}
              onChange={handleChange}
              placeholder={t.linkPlaceholder}
              variant="outlined"
              fullWidth
              sx={commonTextFieldStyle}
            />

            {(formData.platform === 'other' || formData.platform === 'media') && (
              <Box className="full-width">
                <TextField
                  label={formData.platform === 'media' ? t.specifyMedia : t.otherPlatform}
                  name="otherPlatform"
                  value={formData.otherPlatform}
                  onChange={handleChange}
                  placeholder={formData.platform === 'media' ? t.mediaPlaceholder : t.platformPlaceholder}
                  variant="outlined"
                  fullWidth
                  required={formData.platform === 'media'}
                  sx={commonTextFieldStyle}
                />
              </Box>
            )}

            <Box className="full-width">
              <TextField
                label={t.description}
                name="comments"
                value={formData.comments}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                placeholder={t.descriptionPlaceholder}
                required
                sx={commonTextFieldStyle}
              />
            </Box>

            <Box className="full-width">
              <Box sx={{ mt: 2 }}>
                <FormLabel sx={{ display: 'block', mb: 1 }}>
                  {t.screenshot}
                </FormLabel>
                
                <Stack direction="row" spacing={2} alignItems="center">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    multiple
                    style={{ display: 'none' }}
                    id="upload-files"
                  />
                  <label htmlFor="upload-files">
                    <Fab
                      color="primary"
                      size="medium"
                      component="span"
                      aria-label="add files"
                      sx={{
                        bgcolor: theme.primary,
                        boxShadow: 'none',
                        '&:hover': {
                          bgcolor: theme.secondary,
                          transition: 'background-color 0.2s'
                        }
                      }}
                    >
                      <AddIcon />
                    </Fab>
                  </label>
                  <Typography color="text.secondary" variant="body2">
                    {previewUrls.length === 0 
                      ? t.addImages 
                      : `${previewUrls.length} ${previewUrls.length > 1 ? t.imagesSelected : t.imageSelected}`
                    }
                  </Typography>
                </Stack>
                
                {previewUrls.length > 0 && (
                  <ImageList 
                    sx={{ 
                      width: '100%', 
                      mt: 2,
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))!important'
                    }} 
                    gap={8}
                  >
                    {previewUrls.map((url, index) => (
                      <ImageListItem 
                        key={index}
                        sx={{ 
                          border: `1px solid ${theme.primary}20`,
                          borderRadius: '4px',
                          overflow: 'hidden',
                          position: 'relative'
                        }}
                      >
                        <img
                          src={url}
                          alt={`Pièce jointe ${index + 1}`}
                          loading="lazy"
                          style={{ 
                            width: '100%',
                            height: '200px',
                            objectFit: 'cover'
                          }}
                        />
                        {uploadProgress[index] > 0 && uploadProgress[index] < 100 && (
                          <LinearProgress
                            variant="determinate"
                            value={uploadProgress[index]}
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: 4,
                              backgroundColor: `${theme.primary}20`,
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: theme.primary
                              }
                            }}
                          />
                        )}
                        <IconButton
                          onClick={() => handleRemoveFile(index)}
                          disabled={isUploading}
                          sx={{
                            position: 'absolute',
                            right: 4,
                            top: 4,
                            bgcolor: 'white',
                            border: `1px solid ${theme.primary}40`,
                            '&:hover': {
                              bgcolor: theme.background
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ImageListItem>
                    ))}
                  </ImageList>
                )}
              </Box>
            </Box>

            <Box className="full-width">
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isUploading}
                sx={{ 
                  mt: 2,
                  py: 1.5,
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  bgcolor: theme.primary,
                  width: '100%',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: theme.secondary,
                    boxShadow: 'none'
                  },
                  '&.Mui-disabled': {
                    bgcolor: `${theme.primary}60`
                  }
                }}
              >
                {isUploading ? t.uploading || "Uploading..." : t.submit}
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default ReportForm; 